import React, { useEffect, useState, useRef } from 'react';
import Highcharts from 'highcharts';
import classnames from 'classnames';
import HighchartsReact from 'highcharts-react-official';
import { NoDataWithName, SwitchBtn } from '@components';
import { useSelector } from 'react-redux';

import { buildSpiderChartOption } from './spiderchart';
import { buildOrgChartOption } from './orgchart';
import { buildGanttChartOption } from './ganttchart';
import { buildWaterfallChartOption } from './waterfallchart';
// import '@style/components/sectionchart.scss';
import '../../styles/components/sectionchart.scss';

import highchartsGantt from 'highcharts/modules/gantt';
import highchartsSankey from 'highcharts/modules/sankey';
import highchartsOrganization from 'highcharts/modules/organization';
import highchartsmore from 'highcharts/highcharts-more';
import highchartsAccessibility from 'highcharts/modules/accessibility';
import { buildStackedBarChartOption } from '@components/sectionchart/stackedBarChart';
import useTheme from '@hooks/useTheme';

highchartsAccessibility(Highcharts);
highchartsmore(Highcharts);
highchartsGantt(Highcharts);
highchartsSankey(Highcharts);
highchartsOrganization(Highcharts);

Highcharts.setOptions({
  lang: {
    thousandsSep: ',',
  },
});

function getTickByPoint(point) {
  return point.series.yAxis.ticks[point.y];
}
Highcharts.Point.prototype.collapse = function (redraw) {
  getTickByPoint(this).collapse(redraw);
};
Highcharts.Point.prototype.expand = function (redraw) {
  getTickByPoint(this).expand(redraw);
};

const buildChartOption = function (
  inputData,
  chartPattern,
  isGanttCollapsed = false,
  setIsCollapseAllButton = () => {},
  chartRef = {},
  buyerName,
  targetName,
  setChartRedraw,
  isLightMode,
) {
  if (!inputData || (inputData && inputData.length === 0)) {
    return null;
  }

  switch (chartPattern) {
    case 'spider': {
      return buildSpiderChartOption(
        inputData,
        buyerName,
        targetName,
        isLightMode,
      );
    }
    case 'org': {
      return buildOrgChartOption(inputData);
    }
    case 'gantt': {
      return buildGanttChartOption(
        inputData,
        isGanttCollapsed,
        setIsCollapseAllButton,
        chartRef,
        setChartRedraw,
        isLightMode,
      );
    }
    case 'waterfall': {
      return buildWaterfallChartOption(inputData, isLightMode);
    }
    case 'stackedBar': {
      return buildStackedBarChartOption(inputData, isLightMode);
    }
    default:
      // console.log(`no such chart pattern ${chartPattern}.`);
      return null;
  }
};

const collapseGantt = (chartRef, inputData, isGanttCollapsed) => {
  const { chart } = chartRef.current;
  inputData.forEach((element) => {
    if (element.isParent) {
      const parentNode = chart.get(`-L1-${element.name}`);
      if (parentNode && isGanttCollapsed) {
        parentNode.collapse(false);
      }
      if (parentNode && !isGanttCollapsed) {
        parentNode.expand(false);
      }
    }
  });
  chart.redraw();
};

const SectionChart = ({
  inputData,
  chartPattern,
  showInHorizontal = false,
  shouldShowChart,
  showGrid,
  setGridShow,
  hasChartTitle = true,
  hideShowTableBtn = false,
}) => {
  const { theme } = useTheme();
  const isLightMode = theme === 'light';
  const { buyerName, targetName } = useSelector(
    ({ common }) => common.projectCompanyInfo,
  );
  const chartRef = useRef(null);
  const [isGanttCollapsed, setIsGanttCollapsed] = useState(false);
  const [isCollapseAllButton, setIsCollapseAllButton] = useState(true);
  const [fresh, setfresh] = useState(true);
  const [chartRedraw, setChartRedraw] = useState(false);
  const [options, setOptions] = useState(
    buildChartOption(
      inputData,
      chartPattern,
      isGanttCollapsed,
      setIsCollapseAllButton,
      chartRef,
      buyerName,
      targetName,
      setChartRedraw,
      isLightMode,
    ) || {},
  );

  useEffect(() => {
    if (!chartRef || !chartRef.current) {
      return;
    }
    if (chartPattern === 'gantt') {
      collapseGantt(chartRef, inputData, isGanttCollapsed);
    } else {
      // for gantt chart collapse & expand correctly, cannot setoptions in useEffect
      setOptions(
        buildChartOption(
          inputData,
          chartPattern,
          isGanttCollapsed,
          setIsCollapseAllButton,
          chartRef,
          buyerName,
          targetName,
          setChartRedraw,
          isLightMode,
        ) || {},
      );
    }
    const { chart } = chartRef.current;
    chart.reflow();
    chart.redraw();
  }, [
    inputData,
    showInHorizontal,
    isGanttCollapsed,
    chartPattern,
    fresh,
    isLightMode,
  ]);

  if (!inputData || (inputData && inputData.length === 0)) {
    return (
      <div className="section-chart-wrapper">
        <NoDataWithName />
      </div>
    );
  }

  const sitchGanttCollapseAll = () => {
    setIsGanttCollapsed(true);
    setfresh((x) => !x);
  };

  const sitchGanttExpandAll = () => {
    setIsGanttCollapsed(false);
    setfresh((x) => !x);
  };

  const isCollpaseSwitchNeeded = (inputData, isGanttCollapsed) => {
    let result = false;

    const allParentIndexArray = [];

    inputData.forEach((x, index) => {
      if (x.isParent) {
        allParentIndexArray.push(index);
      }
    });
    allParentIndexArray.forEach((e, i, a) => {
      if (i === a.length - 1) {
        if (inputData.slice(e).length > 1) {
          result = true;
        }
      } else if (inputData.slice(e, a[i + 1]).length > 1) {
        result = true;
      }
    });

    return result;
  };

  const findWidgetWrapper = (e) => {
    while (e.parentNode) {
      if (e.parentNode.className?.includes('gantt-chart-content')) {
        return e.parentNode;
      }
      return findWidgetWrapper(e.parentNode);
    }
    return undefined;
  };

  const createLabel = (chartRef) => {
    if (!chartRef.current || !chartRedraw) {
      return null;
    }
    const { chart } = chartRef.current;
    if (chart) {
      //获得外层 content元素
      const widgetWrapper = findWidgetWrapper(chart.container);
      if (widgetWrapper) {
        const containerRect = widgetWrapper?.getBoundingClientRect();
        //获得navigator元素
        const navigator = chart.container.getElementsByClassName(
          'highcharts-navigator',
        );
        if (navigator && navigator[0]) {
          //获得navigator bar绝对位置
          const rect = navigator[0].getBoundingClientRect();
          //生成Timeline Slider label
          return (
            <div
              // className="highcharts-label"
              style={{
                position: 'absolute',
                //navigator bar左边距 - content左边距 - 文本宽
                left: 25,
                //navigator bar上边距 - content上边距
                bottom: 55,
                fontFamily: '&quot;Helvetica Neue&quot;, Arial',
                fontsize: 12,
                // color: '#dbdbdb',
                whiteSpace: 'nowrap',
              }}
            >
              Timeline Slider
            </div>
          );
        }
      }
    }
  };

  switch (chartPattern) {
    case 'gantt': {
      return (
        <div className="section-chart-wrapper">
          <div className="chart-top-wrapper">
            {shouldShowChart ? (
              <div className="chart-display-switch-wrapper">
                <SwitchBtn onClick={setGridShow} checked={showGrid} />
                <span>Show Table</span>
              </div>
            ) : null}
          </div>
          <div className="gantt-collapse-expand" style={{ fontSize: 20 }}>
            {isCollpaseSwitchNeeded(inputData) && !isCollapseAllButton ? (
              <a style={{ fontSize: 14 }} onClick={sitchGanttExpandAll}>
                Expand All
              </a>
            ) : null}
            {isCollpaseSwitchNeeded(inputData) && isCollapseAllButton ? (
              <a style={{ fontSize: 14 }} onClick={sitchGanttCollapseAll}>
                Collapse All
              </a>
            ) : null}
          </div>
          <div
            className={classnames('gantt-chart-content', {
              'highcharts-dark': !isLightMode,
              'highcharts-light': isLightMode,
            })}
          >
            <HighchartsReact
              ref={chartRef}
              highcharts={Highcharts}
              constructorType="ganttChart"
              options={options}
            />
            {createLabel(chartRef)}
          </div>
        </div>
      );
    }

    default:
      return (
        <div className="section-chart-wrapper">
          {hideShowTableBtn ? null : (
            <div className="chart-top-wrapper">
              {shouldShowChart ? (
                <div className="chart-display-switch-wrapper">
                  <SwitchBtn onClick={setGridShow} checked={showGrid} />
                  <span>Show Table</span>
                </div>
              ) : null}
            </div>
          )}
          <div
            className={classnames(`${chartPattern}-chart-content`, {
              'highcharts-dark': !isLightMode,
              'highcharts-light': isLightMode,
            })}
          >
            <HighchartsReact
              ref={chartRef}
              highcharts={Highcharts}
              constructorType="chart"
              options={options}
            />
          </div>
        </div>
      );
  }
};

export default React.memo(SectionChart);
